<template>
  <div class="blur-backdrop">
    <div
      class="modal"
    >
      <div class="title">
        Join Team
      </div>
      <div
        v-if="!(decodedJWT.teamName && decodedJWT.teamLeaderName)"
        class="spinner-container"
      >
        <Spinner
          color="teal"
          class="spinner-large" 
        />
      </div>
      <div v-else>
        <div
          v-if="isUserOnInvitingTeam && !hasInviteLinkExpired"
          class="body"
        >
          <div>
            <div class="explanatory-text">
              You're already a member of this team.
            </div>
            <div
              v-show="'teamName' in decodedJWT"
              class="explanatory-text"
            >
              Team Name: {{ decodedJWT.teamName }}
            </div>
            <div
              v-show="'teamLeaderName' in decodedJWT"
              class="explanatory-text"
            >
              Team Leader: {{ decodedJWT.teamLeaderName }}
            </div>
          </div>
          <SpinnerButton
            class="button"
            @click="redirectToTeamPage"
          >
            View Your Team
          </SpinnerButton>
        </div>
        <div
          v-if="!isUserOnInvitingTeam && isUserLeader && !hasInviteLinkExpired"
          class="body"
        >
          <div>
            <div class="explanatory-text">
              You are the leader of a team.
            </div>
            <div class="explanatory-text">
              You'll need to transfer leadership to one of your team members before you can join another team.
            </div>
          </div>
          <SpinnerButton
            class="button"
            @click="redirectToTeamPage"
          >
            View Your Team
          </SpinnerButton>
        </div>
        <div
          v-if="!isUserOnInvitingTeam && !isUserLeader && !hasInviteLinkExpired"
          class="body"
        >
          <div>
            <div
              v-if="isUserOnTeam"
              class="explanatory-text"
            >
              You've been invited to switch to this team!
            </div>
            <div
              v-if="!isUserOnTeam"
              class="explanatory-text"
            >
              You've been invited to join this team!
            </div>
            <div
              v-show="'teamName' in decodedJWT"
              class="explanatory-text"
            >
              Team Name: {{ decodedJWT.teamName }}
            </div>
            <div
              v-show="'teamLeaderName' in decodedJWT"
              class="explanatory-text"
            >
              Team Leader: {{ decodedJWT.teamLeaderName }}
            </div>
          </div>
          <SpinnerButton
            v-if="isUserOnTeam"
            class="button center"
            :isProcessing="isProcessingRequest"
            :isDisabled="!isJoinButtonEnabled"
            @click="joinTeam"
          >
            Switch to This Team
          </SpinnerButton>
          <SpinnerButton
            v-if="!isUserOnTeam"
            class="button center"
            :isProcessing="isProcessingRequest"
            :isDisabled="!isJoinButtonEnabled"
            @click="joinTeam"
          >
            Join This Team
          </SpinnerButton>
        </div>
        <div
          v-if="hasInviteLinkExpired"
          class="body"
        >
          <div>
            <div class="explanatory-text">
              This invite link has expired.
              If you'd still like to join this team, please contact the team leader for a new invite link.
              If you don't want to join this team, simply ignore this message.
            </div>
          </div>
          <!-- <div
            class="button"
            @click="redirectToHomePage"
          >
            Return to Your Home Page
          </div> -->
          <SpinnerButton
            class="button"
            @click="redirectToHomePage"
          >
            Return to Your Home Page
          </SpinnerButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Spinner from '@/components/utilComponents/Spinner';
import SpinnerButton from '@/components/utilComponents/SpinnerButton.vue';

export default Vue.extend({
  name: 'JoinTeamView',
  components: {
    Spinner,
    SpinnerButton
  },
  data: () => ({
    token: '',
    decodedJWT: {},
    inviteLink: '',
    teamIdForUser: '',
    hasInviteLinkExpired: false,
    isUserOnTeam: false,
    isUserLeader: false,
    isJoinButtonEnabled: false,
    isProcessingRequest: false,
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    isUserOnInvitingTeam: function() {
      return this.teamIdForUser && this.teamIdForUser.length > 0 && this.decodedJWT.teamId === this.teamIdForUser;
    }
  },
  methods: {
    async verifyJWT() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/verifyjwt`, {
          token: this.token
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });

        this.decodedJWT = response.data.decoded;
        this.isJoinButtonEnabled = true;
      } catch (error) {
        // NOTE: the backend call will fail if and only if the JWT has expired
        console.log('Error:', error);
        this.hasInviteLinkExpired = true;
      }
    },
    async joinTeam() {
      if (!this.isProcessingPayment && this.decodedJWT.teamId && this.decodedJWT.teamId.length > 0) {
        try {
          this.isProcessingRequest = true;
          await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/jointeam`, {
            auth0UserId: this.auth0UserId,
            teamId: this.decodedJWT.teamId
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });

          // show success message with link to account
          this.$emit('check-user-is-on-team');
        } catch (error) {
          console.log('Error:', error);
          console.log(error.response);
          this.isProcessingRequest = false;
        }
      }

      this.redirectToHomePage();
    },
    async getTeamIdForUser() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getteamidforuser`, {
          auth0UserId: this.auth0UserId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });

        this.teamIdForUser = response.data.teamId;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async checkUserIsOnTeam() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/checkuserisonteam`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });

      this.isUserOnTeam = response.data.isUserOnTeam;
      this.isUserLeader = response.data.isUserLeader;
    },
    redirectToHomePage() {
      this.$router.push({
        name: 'home'
      });
    },
    redirectToTeamPage() {
      this.$router.push({
        name: 'team'
      });
    }
  },
  created: async function() {
    if ('token' in this.$route.query) {
      this.token = this.$route.query.token;
    }
  },
  mounted: async function() {
    try {
      await this.checkUserIsOnTeam();
      await this.getTeamIdForUser();
      await this.verifyJWT();
    } catch (error) {
      console.log('Error:', error);
    }
  }
})
</script>

<style scoped lang="scss">
.blur-backdrop {
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  backdrop-filter: blur(0.6em);
}

.modal {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  width: 30em;
  height: 20em;
  padding: 0.5em 1em;
  border: 0.1em solid $dim-gray;
  border-radius: 0.4em;
  box-shadow: 0 6px 8px -4px black;
}

.title {
  align-self: center;
  font-size: 1.5em;
  color: $brand-teal;
  margin: 0.8em 0;
}

.body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.explanatory-text {
  margin: 0.8em 1em;
  max-width: 100%;
  font-size: 1.1em;
  text-align: center;
}

.button {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: $natural-white;
  background-color: $brand-teal;
  font-size: 1.2em;
  padding: 0.6em 0.8em;
  margin: 0.2em 0;
  width: 100%;
  border-radius: 0.2em;
  cursor: pointer;
}

.button:hover {
  filter: brightness(1.15);
}

.button.center {
  flex-direction: row;
  justify-content: center;
  margin: 0.2em auto;
}

.button.disabled {
  background-color: $light-gray;
  pointer-events: none;
}

.button.no-pointer-events {
  pointer-events: none;
}

.spinner-container {
  padding-top: 4em;
  height: 100%;
}

.spinner-padding {
  padding: 0 0.4em;
}

.spinner-large {
  height: 8em;
  width: 8em;
}

</style>
